<script lang="ts">
  import { blogFilter } from "$stores";
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import Section from "$lib/molecules/Section.svelte";

  import ArrowLink from "$lib/atoms/ArrowLink.svelte";

  import Button from "$lib/atoms/Button.svelte";
  import { ButtonColor, IconType } from "$types/atoms";
  import Icon from "$lib/atoms/Icon.svelte";

  import FluidGrid from "$lib/molecules/newsroom/grids/FluidGrid.svelte";
  import BlockGrid from "$lib/molecules/newsroom/grids/BlockGrid.svelte";
  import InlineGrid from "$lib/molecules/newsroom/grids/InlineGrid.svelte";
  import InlinePressReleasesGrid from "$lib/molecules/newsroom/grids/InlinePressReleasesGrid.svelte";

  import TwoColResearchInsightsGrid from "$lib/molecules/newsroom/grids/TwoColResearchInsightsGrid.svelte";
  import ResearchInsightsGrid from "$lib/molecules/newsroom/grids/ResearchInsightsGrid.svelte";
  import PodcastsSlider from "$lib/molecules/newsroom/grids/PodcastsSlider.svelte";
  import MediaCoverageSlider from "$lib/molecules/newsroom/grids/MediaCoverageSlider.svelte";
  import MediaCoverageGrid from "$lib/molecules/newsroom/grids/MediaCoverageGrid.svelte";
  import FeaturedVideosGrid from "$lib/molecules/newsroom/grids/FeaturedVideosGrid.svelte";

  import { onMount } from "svelte";
  import PressKitGrid from "$lib/molecules/newsroom/grids/PressKitGrid.svelte";
  import RichText from "$lib/molecules/RichText.svelte";
  import type { NewsroomListStoryblok } from "$types/components";
  import type { PostsResponse } from "$types/data";
  import SimpleGrid from "$lib/molecules/newsroom/grids/SimpleGrid.svelte";

  export let blok: NewsroomListStoryblok;
  export let related = false;
  export let grid = true;

  let wrapper: HTMLDivElement;
  let page = 1;
  let posts: PostsResponse["data"]["stories"] = [];
  let morePostsToFetch = true;
  let sortFeaturedPostsBy = blok.sortFeaturedPostsBy;
  let loading = false;
  let topPaddingClass: string | undefined;

  const getPosts = async () => {
    loading = true;

    const getBody = () => {
      // Get latest posts in category
      if (blok.type === "latest" && blok.latestPostsCategory?.full_slug) {
        return {
          starts_with: blok.latestPostsCategory.full_slug,
          per_page: blok.limit,
          page,
        };
      }

      // Get featured posts by ordered uuid
      if (
        blok.type === "featured" &&
        blok.featuredPosts &&
        blok.featuredPosts.length > 0 &&
        !sortFeaturedPostsBy
      ) {
        return {
          by_uuids_ordered: blok.featuredPosts.join(","),
          per_page: blok.limit,
          page,
        };
      }

      // Get featured posts by sorted uuid
      if (
        blok.type === "featured" &&
        blok.featuredPosts &&
        blok.featuredPosts.length > 0 &&
        sortFeaturedPostsBy
      ) {
        return {
          by_uuids: blok.featuredPosts.join(","),
          per_page: blok.limit,
          sort_by: sortFeaturedPostsBy,
          page,
        };
      }

      // Get posts matching the filter specified in global store
      if (blok.type === "filtered") {
        const body: {
          per_page: number | undefined;
          page: number;
          sort_by: string;
          filter_query: Record<string, unknown>;
        } = {
          per_page: blok.limit,
          page,
          sort_by: "first_published_at:desc",
          filter_query: {
            showInBlog: { is: true },
          },
        };

        if ($blogFilter?.field) {
          body.filter_query[$blogFilter.field] = {
            all_in_array: $blogFilter.filter,
          };
        }

        return body;
      }
    };

    const body = getBody();

    if (!body) {
      return;
    }

    const res = await fetch("/api/posts", {
      method: "POST",
      body: JSON.stringify(body),
    });

    const data: PostsResponse = await res.json();
    const stories = data?.data?.stories ?? [];

    if (stories.length < (blok.limit ?? 0)) {
      morePostsToFetch = false;
    }

    if (blok.type === "filtered") {
      posts = stories;
    } else {
      posts.push(...stories);
      posts = posts;
    }
    loading = false;
  };

  onMount(() => {
    getPosts();

    if (blok.type === "filtered") {
      blogFilter.subscribe(() => {
        page = 1;
        posts = [];
        morePostsToFetch = true;
        getPosts();
      });
    }
  });

  $: {
    if (blok.sectionClasses) {
      topPaddingClass = blok.sectionClasses;
    } else if (related) {
      topPaddingClass = "pt-small";
    } else if (blok.topMargin === "none") {
      topPaddingClass = "top-pad-none";
    } else if (blok.topMargin === "xsmall") {
      topPaddingClass = "top-pad-xsmall";
    } else if (blok.topMargin === "small") {
      topPaddingClass = "top-pad-small";
    } else if (blok.topMargin === "medium") {
      topPaddingClass = "top-pad-medium";
    } else if (blok.topMargin === "large") {
      topPaddingClass = "top-pad-large";
    } else if (blok.topMargin === "extra-large") {
      topPaddingClass = "top-pad-extra-large";
    } else if (blok.topMargin === "2x-extra-large") {
      topPaddingClass = "top-pad-2x-extra-large";
    } else {
      topPaddingClass = "pt-mid";
    }
  }

  const anchorLink = (blok.heading?.toLowerCase() ?? "").replace(/\s+/g, "-");
</script>

<div bind:this={wrapper} class="max-w-[100vw] overflow-hidden">
  <Section
    class={`section ${
      blok?.layout === "featured-videos-grid" || blok?.layout === "podcasts"
        ? "overflow-visible "
        : "overflow-hidden "
    }
        ${grid === false ? "!px-0" : ""}
        ${topPaddingClass}
        ${blok.layout === "fluid-grid" ? "section-header-width" : ""}
    `}
    {blok}
  >
    <GridContainer noPadding={grid === false || blok.layout === "fluid-grid"}>
      <GridItem col={12} colMd={12} class={grid === false ? "!p-0" : ""}>
        <div class="relative">
          {#if blok.heading}
            <div
              class="flex flex-wrap justify-between items-end mb-[24px] sm:mb-[32px] gap-5"
            >
              <div class="md:max-w-[55%] mr-8 relative">
                <a id={anchorLink} class="invisible">{blok.heading}</a>
                <svelte:element
                  this={blok.headingLevel === "h1" ? "h1" : "h2"}
                  class={blok?.largeHeading ? "type:h2" : "type:h3"}
                >
                  {@html blok.heading}
                </svelte:element>
                {#if blok.content?.content?.[0]?.content}
                  <div class="!mt-[14px]">
                    <RichText {blok} />
                  </div>
                {/if}
              </div>

              {#if blok?.seeMoreLink?.cached_url}
                <div>
                  {#if blok?.subHeading}
                    <div>
                      {#if blok.layout === "fluid-grid"}
                        <a
                          class="type:body-small text-charcoal flex gap-[4px] items-center"
                          href={blok.seeMoreLink.url ||
                            "/" + blok.seeMoreLink.cached_url}
                        >
                          {blok.subHeading}
                          <Icon type={IconType.CHEVRON_RIGHT} />
                        </a>
                      {:else}
                        <h3 class="type:body-large text-charcoal">
                          {blok.subHeading}
                        </h3>
                        <a
                          href={blok?.seeMoreLink?.url?.includes("@")
                            ? "mailto:" + blok.seeMoreLink.url
                            : blok.seeMoreLink.url}
                          class="type:body-large !font-bold text-charcoal"
                          >{blok.seeMoreLink.url}
                        </a>
                      {/if}
                    </div>
                  {:else}
                    <ArrowLink
                      class="reduced hover-red-bg"
                      href={blok.seeMoreLink}>See More</ArrowLink
                    >
                  {/if}
                </div>
              {/if}
            </div>
          {/if}

          {#if blok.layout === "fluid-grid"}
            <FluidGrid {blok} {posts} />
          {/if}
          {#if blok.layout === "simple-grid"}
            <SimpleGrid {blok} {posts} />
          {/if}
          {#if blok.layout === "block-grid"}
            <BlockGrid {posts} />
          {/if}
          {#if blok.layout === "inline-grid"}
            <InlineGrid {posts} />
          {/if}
          {#if blok.layout === "inline-press-releases-grid"}
            <InlinePressReleasesGrid {posts} />
          {/if}
          {#if blok.layout === "two-col-research-insights"}
            <TwoColResearchInsightsGrid {posts} />
          {/if}
          {#if blok.layout === "research-insights-grid"}
            <ResearchInsightsGrid {posts} />
          {/if}
          {#if blok.layout === "podcasts"}
            <PodcastsSlider {posts} />
          {/if}
          {#if blok.layout === "media-coverage-slider"}
            <MediaCoverageSlider {posts} />
          {/if}
          {#if blok.layout === "media-coverage-grid"}
            <MediaCoverageGrid {posts} />
          {/if}
          {#if blok.layout === "featured-videos-grid"}
            <FeaturedVideosGrid {posts} />
          {/if}
          {#if blok.layout === "press-kit"}
            <PressKitGrid {posts} />
          {/if}
          <!-- {/if}  -->

          {#if posts.length > 0 && (morePostsToFetch || page > 1) && blok?.showLoadMoreButton === true}
            <div class="mt-[2.5rem] flex justify-center gap-[8px]">
              {#if page > 1}
                <Button
                  disabled={loading}
                  on:click={() => {
                    page -= 1;
                    morePostsToFetch = true;
                    getPosts().then(() => wrapper.scrollIntoView());
                  }}
                  iconLeft
                  icon={IconType.ARROW_LEFT_LARGE}
                  reduced
                  color={ButtonColor.SAND}
                  class="text-charcoal"
                >
                  {loading ? "Loading..." : "Prev"}
                </Button>
              {/if}
              {#if morePostsToFetch}
                <Button
                  disabled={loading}
                  on:click={() => {
                    page += 1;
                    getPosts().then(() => wrapper.scrollIntoView());
                  }}
                  icon={IconType.ARROW_RIGHT_LARGE}
                  reduced
                  color={ButtonColor.BLACK}
                >
                  {loading ? "Loading..." : "Next"}
                </Button>
              {/if}
            </div>
          {/if}
        </div>
      </GridItem>
    </GridContainer>
  </Section>
</div>
